import { isBurlingtonElectric } from './customerRequest';

/**
 * The form route of customer_request: the utility takes usage-data requests
 * through its own website form, and that form accepts its fields pre-filled
 * from the URL. Burlington Electric Department moved to this on 2026-10-23
 * (Paul Nadeau) and enabled pre-fill on 2026-10-26 (Adam Rabin).
 *
 * The URL lives on the utility row (`utilities.request_form_template`, edited
 * in Admin > Utilities) with {placeholders}; this module fills them. It only
 * ever fills what the project already holds. Anything unknown is left blank
 * for the homeowner, and the waiver is never touched: the homeowner signs it.
 */

/** The placeholders a template may carry. Anything else is filled blank. */
export const REQUEST_FORM_FIELDS = [
  'account',
  'startdate',
  'enddate',
  'first_name',
  'last_name',
  'email',
  'phone',
  'address',
  'data_recipient_name',
  'data_recipient_email',
] as const;

export type RequestFormField = (typeof REQUEST_FORM_FIELDS)[number];
export type RequestFormValues = Partial<Record<RequestFormField, string>>;

/**
 * BED's commitment, from the date the customer submits the form (Paul Nadeau,
 * 2026-10-23). Business days skip weekends; holidays are not counted.
 */
export const REQUEST_FORM_BUSINESS_DAYS = 3;

/** True when the template is an https URL, so it is safe to link to. */
export function isUsableTemplate(template: string | null | undefined): template is string {
  const value = (template ?? '').trim();
  if (!value) return false;
  try {
    return new URL(value.replace(/\{[a-z_]+\}/g, '')).protocol === 'https:';
  } catch {
    return false;
  }
}

/**
 * URL-encode a value. `@` stays readable, as in BED's own example link
 * (data_recipient_email=bob@bob.com); browsers and BED's form read it either way.
 */
function encodeValue(value: string): string {
  return encodeURIComponent(value).replace(/%40/g, '@');
}

/** Fill every {placeholder} in the template; an unknown value fills blank. */
export function fillRequestForm(template: string, values: RequestFormValues): string {
  return template.trim().replace(/\{([a-z_]+)\}/g, (_, key: string) => {
    const value = (values as Record<string, string | undefined>)[key];
    return encodeValue((value ?? '').trim());
  });
}

// ── Dates ──────────────────────────────────────────────────────────────────

export interface CalendarDate {
  year: number;
  /** 1–12 */
  month: number;
  day: number;
}

function daysInMonth(year: number, month: number): number {
  return new Date(Date.UTC(year, month, 0)).getUTCDate();
}

function iso({ year, month, day }: CalendarDate): string {
  return `${String(year).padStart(4, '0')}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

/** The calendar date in the viewer's own time zone. */
export function localDate(now: Date): CalendarDate {
  return { year: now.getFullYear(), month: now.getMonth() + 1, day: now.getDate() };
}

/**
 * The most recent 13 months, ending yesterday: today's date 13 months back
 * (clamped to the end of a shorter month) through yesterday. 220.87 needs 12
 * months; the 13th is margin for a gap at either end.
 */
export function requestDateRange(today: CalendarDate): { startdate: string; enddate: string } {
  const y = new Date(Date.UTC(today.year, today.month - 1, today.day - 1));
  const end: CalendarDate = { year: y.getUTCFullYear(), month: y.getUTCMonth() + 1, day: y.getUTCDate() };

  const monthIndex = today.year * 12 + (today.month - 1) - 13;
  const startYear = Math.floor(monthIndex / 12);
  const startMonth = (monthIndex % 12) + 1;
  const start: CalendarDate = {
    year: startYear,
    month: startMonth,
    day: Math.min(today.day, daysInMonth(startYear, startMonth)),
  };
  return { startdate: iso(start), enddate: iso(end) };
}

/** The calendar date of an instant in a given time zone. */
function dateInZone(instant: Date, timeZone: string): CalendarDate {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
  }).formatToParts(instant);
  const get = (type: string) => Number(parts.find(p => p.type === type)?.value);
  return { year: get('year'), month: get('month'), day: get('day') };
}

/**
 * The date the utility's data is expected: the submission's date in the
 * utility's time zone, plus `businessDays` weekdays. A Saturday or Sunday
 * submission counts from Monday. Returned as YYYY-MM-DD.
 */
export function expectedByDate(
  submittedAt: string | Date,
  businessDays: number = REQUEST_FORM_BUSINESS_DAYS,
  timeZone = 'America/New_York',
): string {
  const start = dateInZone(new Date(submittedAt), timeZone);
  const d = new Date(Date.UTC(start.year, start.month - 1, start.day));
  let left = businessDays;
  while (left > 0) {
    d.setUTCDate(d.getUTCDate() + 1);
    const dow = d.getUTCDay();
    if (dow !== 0 && dow !== 6) left -= 1;
  }
  return iso({ year: d.getUTCFullYear(), month: d.getUTCMonth() + 1, day: d.getUTCDate() });
}

/** "Thursday, October 29" for a YYYY-MM-DD date. */
export function formatExpectedBy(date: string): string {
  const [y, m, d] = date.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    timeZone: 'UTC',
  });
}

// ── Values from the request ────────────────────────────────────────────────

/** "Mary Ann Smith" → first "Mary Ann", last "Smith". One word is a first name. */
export function splitName(fullName: string | null | undefined): { first: string; last: string } {
  const parts = (fullName ?? '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return { first: '', last: '' };
  if (parts.length === 1) return { first: parts[0], last: '' };
  return { first: parts.slice(0, -1).join(' '), last: parts[parts.length - 1] };
}

/** A US number as 555-555-5555; anything else exactly as typed. */
export function formatPhone(phone: string | null | undefined): string {
  const raw = (phone ?? '').trim();
  let digits = raw.replace(/\D/g, '');
  if (digits.length === 11 && digits.startsWith('1')) digits = digits.slice(1);
  if (digits.length === 10) return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
  return raw;
}

/**
 * The account as the utility's form wants it. BED's is "CustomerID-LocationID"
 * (1234-5678), and half of it would send BED looking for the wrong meter, so a
 * BED account missing either part is left blank for the homeowner.
 */
export function formAccount(utilityName: string, accountNumber: string | null | undefined): string {
  const value = (accountNumber ?? '').trim();
  if (!value) return '';
  if (isBurlingtonElectric(utilityName)) {
    const dash = value.indexOf('-');
    if (dash === -1) return '';
    const customerId = value.slice(0, dash).trim();
    const locationId = value.slice(dash + 1).trim();
    return customerId && locationId ? `${customerId}-${locationId}` : '';
  }
  return value;
}

/** "Acme Electric (via TrueLoad)": who the utility sends the data to. */
export function recipientName(companyName: string | null | undefined, contractorName: string | null | undefined): string {
  const who = (companyName ?? '').trim() || (contractorName ?? '').trim();
  return who ? `${who} (via TrueLoad)` : '';
}

export interface RequestFormInput {
  utilityName: string;
  customerName: string;
  customerEmail: string | null | undefined;
  customerPhone: string | null | undefined;
  serviceAddress: string;
  accountNumber: string | null | undefined;
  contractorName: string;
  companyName: string;
  /** Where the utility sends the file: the contractor's email, until TrueLoad receives it itself. */
  recipientEmail: string | null | undefined;
}

/** Every value the request holds, mapped to the form's fields. */
export function requestFormValues(input: RequestFormInput, today: CalendarDate): RequestFormValues {
  const { first, last } = splitName(input.customerName);
  return {
    account: formAccount(input.utilityName, input.accountNumber),
    ...requestDateRange(today),
    first_name: first,
    last_name: last,
    email: (input.customerEmail ?? '').trim(),
    phone: formatPhone(input.customerPhone),
    address: input.serviceAddress.trim(),
    data_recipient_name: recipientName(input.companyName, input.contractorName),
    data_recipient_email: (input.recipientEmail ?? '').trim(),
  };
}
